import { Alert, Grid2, List, ListItem, Stack } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useHeaderContext, ValidationType } from '../../../contexts/HeaderContext';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormField, FormFieldType } from '@enroller/admissions-components';
import { usePageTitle } from '@enroller/utils';
import { TitledSection } from '../../../layout/TitledSection';
import { zodResolver } from '@hookform/resolvers/zod';
import { DearFamilyFormValues, dearFamilySchema, defaultValues } from './DearFamilyFormValues';
import { useApplicationContext } from '../../../contexts/ApplicationContext/ApplicationContext';

export const DearFamilyForm = () => {
  const { setSubmit, setHeaderTitle, setIsFormLoading } = useHeaderContext();
  const { setPageTitle } = usePageTitle();
  const [validationMode, setValidationMode] = useState<ValidationType | ''>('');
  const { setApplicationPageData, triggerErrors, displayErrors } = useApplicationContext();

  useEffect(() => {
    setHeaderTitle({ title: 'Dear Family', subtitle: '' });
    setPageTitle('Dear Family');
  }, [setHeaderTitle, setPageTitle]);

  const resolver = useMemo(() => {
    const currentSchema = validationMode === ValidationType.Full ? dearFamilySchema.required : dearFamilySchema.optional;
    return zodResolver(currentSchema);
  }, [validationMode]);

  const { control, handleSubmit, trigger } = useForm<DearFamilyFormValues>({
    defaultValues,
    resolver,
  });

  // Trigger errors if they have been triggered in another form
  useEffect(() => {
    if (displayErrors) setValidationMode(ValidationType.Full);
  }, [displayErrors, trigger]);

  const onSubmit = useCallback(
    () =>
      handleSubmit(
        (data) => {
          setApplicationPageData('dearFamily', data);
          if (validationMode === ValidationType.Full) triggerErrors();
          setIsFormLoading(true);
          setTimeout(() => {
            console.log('Form submitted:', data);
            setIsFormLoading(false);
          }, 1000);
        },
        () => triggerErrors(),
      )(),
    [handleSubmit, setIsFormLoading, setApplicationPageData, triggerErrors, validationMode],
  );

  // Submit form when resolver is changed.
  useEffect(() => {
    if (validationMode) {
      onSubmit();
      setValidationMode('');
    }
  }, [resolver, validationMode, onSubmit]);

  useEffect(() => {
    setSubmit(setValidationMode);
  }, [setSubmit, setValidationMode]);

  return (
    <Stack component="form" onSubmit={onSubmit} gap={2}>
      <TitledSection title="Dear Family Letter">
        <Grid2 size={12}>
          <Alert variant="outlined" severity="info">
            Writing Your "Dear Host Family" Letter Your letter to your host family is one of the most important parts of your application. It helps your future
            host family get to know you and understand what makes you unique! Please write this letter yourself, without help from an English teacher or anyone
            else fluent in English. We do NOT expect perfect English—what matters most is that your letter is personal and honest.
            <strong>PLEASE ENSURE THE LETTER IS AT LEAST 750 WORDS IN LENGTH.</strong> <br />
            Important Privacy Rules <br />
            For your safety, DO NOT include: <br />
            <List sx={{ listStyleType: 'none', pl: 0, py: 0, '& .MuiListItem-root': { display: 'list-item', p: 0 } }}>
              <ListItem>❌ Your last name (surname)</ListItem>
              <ListItem>❌ The name of the town or city you live in currently</ListItem>
              <ListItem>❌ The name of your school or any clubs you belong to</ListItem>
              <ListItem>❌ The ages of your siblings</ListItem>
              <ListItem>❌ A link to any personal videos</ListItem>
            </List>
            What to Include:
            <br />
            Here are some topics you can write about:
            <br />
            <List sx={{ listStyleType: 'none', pl: 0, py: 0, '& .MuiListItem-root': { display: 'list-item', p: 0 } }}>
              <ListItem>✔️ Your personality &ndash; What makes you, YOU? Are you outgoing, creative, funny, adventurous?</ListItem>
              <ListItem>
                ✔️ Your hobbies and interests &ndash; What do you love to do in your free time? (Sports, music, reading, drawing, volunteering, etc.)
              </ListItem>
              <ListItem>✔️ Your family life &ndash; How do you spend time with your family? What traditions do you enjoy?</ListItem>
              <ListItem>✔️ Your daily life &ndash; What is a typical day like for you?</ListItem>
              <ListItem>✔️ Why you want to be an exchange student &ndash; What excites you most about living in another country?</ListItem>
              <ListItem>✔️ What you hope to share with your host family &ndash; Your culture, traditions, favorite foods, or hobbies</ListItem>
              <ListItem>
                ✔️ Things you would like to learn or experience &ndash; Are there activities, traditions, or skills you are excited to try in your host country?
                (For example, trying new foods, learning a new sport, celebrating different holidays, or joining family activities)
              </ListItem>
            </List>
            Remember, this letter is not about listing achievements—it&apos;s about showing your future host family who you are! Keep it friendly, personal, and
            positive.
          </Alert>
        </Grid2>
        <Grid2 size={12}>
          <FormField type={FormFieldType.Text} label="Letter" name="letter" control={control} maxWords={750} multiline rows={6} />
        </Grid2>
      </TitledSection>
    </Stack>
  );
};
